import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Tooltip, Tag, Select, Row, Col, Form, Input } from 'antd';
import { AiFillDelete, AiFillEdit, AiOutlineSend } from 'react-icons/ai';
import { toast, ToastContainer } from 'react-toastify';
import { Container } from 'react-bootstrap';
import { connect } from 'react-redux';
import { BreathAPIs } from '../../APIs/BreathAPIs';
import MessageTemplateForm from './MessageTemplateForm';
import styles from './styles.module.css';

const MessageTemplates = ({ type }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [channelFilter, setChannelFilter] = useState(null);
    const [addModal, setAddModal] = useState(false);
    const [editTemplate, setEditTemplate] = useState(null);
    const [deleteTemplate, setDeleteTemplate] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [sendModal, setSendModal] = useState(false);
    const [sendTemplate, setSendTemplate] = useState(null);
    const [sendRecipient, setSendRecipient] = useState('');
    const [sendVariables, setSendVariables] = useState({});
    const [sendLoading, setSendLoading] = useState(false);
    const [sendError, setSendError] = useState('');
    const [activeVariable, setActiveVariable] = useState(null);

    const inputRefs = useRef({});

    useEffect(() => {
        fetchTemplates();
    }, [channelFilter]);

    const fetchTemplates = async () => {
        setIsLoading(true);
        try {
            const res = await BreathAPIs.getMessageTemplateList({ channel: channelFilter });
            setTemplates(res.data.data || []);
        } catch (error) {
            toast.error('Error fetching templates.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await BreathAPIs.deleteMessageTemplate(deleteTemplate._id);
            toast.success('Template deleted.');
            fetchTemplates();
        } catch (error) {
            toast.error('Error deleting template.');
        } finally {
            setIsDeleting(false);
            setDeleteTemplate(null);
        }
    };

    const extractVariables = (body) => {
        if (!body) return [];
        const matches = [...body.matchAll(/{{\s*(\w+)\s*}}/g)];
        const unique = [...new Set(matches.map((m) => m[1].trim()))];
        return unique;
    };

    const resolveTemplateBody = (body, variables) => {
        if (!body || !variables) return body;
        return Object.entries(variables).reduce((acc, [key, val]) => {
            const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
            return acc.replace(regex, val || `{{${key}}}`);
        }, body);
    };

    const renderSmsWithVariables = (body, sendVariables, onVariableClick) => {
        if (!body) return null;
        const parts = body.split(/{{\s*(\w+)\s*}}/g);
        return parts.map((part, i) => {
            if (i % 2 === 1) {
                const key = part.trim();
                const filled = sendVariables[key];
                return filled ? (
                    <span
                        key={i}
                        style={{
                            background: '#e6f4ff',
                            color: '#1677ff',
                            borderRadius: 4,
                            padding: '1px 6px',
                            cursor: 'pointer',
                            fontWeight: 500,
                            border: '1px solid #91caff',
                        }}
                        onClick={() => onVariableClick(key)}
                    >
                        {filled}
                    </span>
                ) : (
                    <span
                        key={i}
                        style={{
                            background: '#fff7e6',
                            color: '#d46b08',
                            borderRadius: 4,
                            padding: '1px 6px',
                            cursor: 'pointer',
                            fontWeight: 500,
                            border: '1px dashed #ffc069',
                        }}
                        onClick={() => onVariableClick(key)}
                    >
                        {`{{${key}}}`}
                    </span>
                );
            }
            return <span key={i}>{part}</span>;
        });
    };

    const openSendModal = (record, focusVar = null) => {
        inputRefs.current = {};
        setSendTemplate(record);
        setSendRecipient('');
        setSendVariables({});
        setSendError('');
        setActiveVariable(focusVar);
        setSendModal(true);
    };

    const handleSendTemplate = async () => {
        if (!sendRecipient.trim()) {
            setSendError('Please enter a recipient address or number.');
            return;
        }
        setSendLoading(true);
        try {
            const payload = {
                recipient: sendRecipient.trim(),
                channel: sendTemplate?.channel,
                variables: sendVariables,
            };
            await BreathAPIs.sendMessageTemplate(sendTemplate._id, payload);
            toast.success('Template triggered successfully.');
            setSendModal(false);
            setSendVariables({});
        } catch (error) {
            toast.error('Error sending template. Please check recipient and try again.');
        } finally {
            setSendLoading(false);
        }
    };

    const renderVariableChips = (bodyVariables) => {
        if (!bodyVariables.length) return null;
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {bodyVariables.map((key) => {
                    const filled = sendVariables[key];
                    return filled ? (
                        <span
                            key={key}
                            style={{
                                background: '#e6f4ff',
                                color: '#1677ff',
                                borderRadius: 4,
                                padding: '2px 10px',
                                cursor: 'pointer',
                                fontWeight: 500,
                                border: '1px solid #91caff',
                                fontSize: 13,
                            }}
                            onClick={() => {
                                setActiveVariable(key);
                                setTimeout(() => inputRefs.current[key]?.focus(), 50);
                            }}
                        >
                            {filled}
                        </span>
                    ) : (
                        <span
                            key={key}
                            style={{
                                background: '#fff7e6',
                                color: '#d46b08',
                                borderRadius: 4,
                                padding: '2px 10px',
                                cursor: 'pointer',
                                fontWeight: 500,
                                border: '1px dashed #ffc069',
                                fontSize: 13,
                            }}
                            onClick={() => {
                                setActiveVariable(key);
                                setTimeout(() => inputRefs.current[key]?.focus(), 50);
                            }}
                        >
                            {`{{${key}}}`}
                        </span>
                    );
                })}
            </div>
        );
    };

    const renderVariableInputPanel = () => {
        if (!activeVariable) return null;
        return (
            <div style={{
                marginTop: 10,
                padding: '10px 12px',
                background: '#fff7e6',
                border: '1px solid #ffc069',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
            }}>
                <code style={{ fontSize: 12, color: '#c7254e', whiteSpace: 'nowrap' }}>
                    {`{{${activeVariable}}}`}
                </code>
                <Input
                    ref={(el) => { if (el) inputRefs.current[activeVariable] = el; }}
                    autoFocus
                    placeholder={`Enter value for ${activeVariable}`}
                    value={sendVariables[activeVariable] || ''}
                    onChange={(e) => setSendVariables((prev) => ({ ...prev, [activeVariable]: e.target.value }))}
                    onPressEnter={() => setActiveVariable(null)}
                    suffix={
                        <span
                            style={{ fontSize: 11, color: '#1677ff', cursor: 'pointer' }}
                            onClick={() => setActiveVariable(null)}
                        >
                            Done
                        </span>
                    }
                    style={{ flex: 1 }}
                />
            </div>
        );
    };

    const bodyVariables = extractVariables(sendTemplate?.body);

    const columns = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            width: 220,
            render: (text, record) => (
                <span
                    className={styles['support-view']}
                    onClick={() => setPreviewTemplate(record)}
                >
                    {text}
                </span>
            ),
        },
        {
            title: 'Channel',
            dataIndex: 'channel',
            key: 'channel',
            width: 90,
            render: (val) => (
                <Tag color={val === 'email' ? 'blue' : 'green'}>
                    {val.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            key: 'subject',
            width: 220,
            render: (val) => val || <span style={{ color: '#aaa' }}>—</span>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (val) => val || <span style={{ color: '#aaa' }}>—</span>,
        },
        {
            title: 'Variables',
            dataIndex: 'variables',
            key: 'variables',
            width: 200,
            render: (vars, record) =>
                vars && vars.length > 0
                    ? vars.map((v) => (
                        <Tooltip key={v} title={`Click to fill {{${v.trim()}}}`}>
                            <Tag
                                color="volcano"
                                style={{ cursor: 'pointer', marginBottom: 4 }}
                                onClick={() => openSendModal(record, v.trim())}
                            >
                                {`{{${v.trim()}}}`}
                            </Tag>
                        </Tooltip>
                    ))
                    : <span style={{ color: '#aaa' }}>—</span>,
        },
        {
            title: 'Active',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 80,
            render: (val) => (
                <Tag color={val ? 'success' : 'default'}>{val ? 'Yes' : 'No'}</Tag>
            ),
        },
        {
            title: 'Updated',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 160,
            render: (val) => val ? new Date(val).toLocaleString() : '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 140,
            fixed: 'right',
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Tooltip title="Send">
                        <Button
                            shape="circle"
                            type="primary"
                            icon={<AiOutlineSend />}
                            onClick={() => openSendModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            shape="circle"
                            icon={<AiFillEdit />}
                            onClick={() => setEditTemplate(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            shape="circle"
                            danger
                            icon={<AiFillDelete />}
                            onClick={() => setDeleteTemplate(record)}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <Container>
            <ToastContainer />

            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Select
                        allowClear
                        placeholder="Filter by channel"
                        style={{ width: 180 }}
                        onChange={(val) => setChannelFilter(val || null)}
                        options={[
                            { value: 'email', label: 'Email' },
                            { value: 'sms', label: 'SMS' },
                        ]}
                    />
                </Col>
                <Col>
                    <Button type="primary" onClick={() => setAddModal(true)}>
                        New Template
                    </Button>
                </Col>
            </Row>

            <Table
                loading={isLoading}
                dataSource={templates}
                columns={columns}
                rowKey="_id"
                bordered
                size="middle"
                pagination={{ pageSize: 15, showSizeChanger: false }}
                scroll={{ x: 'calc(700px + 50%)' }}
                className={styles['data-list']}
            />

            {/* Add modal */}
            <MessageTemplateForm
                visible={addModal}
                onClose={() => setAddModal(false)}
                onSuccess={() => { setAddModal(false); fetchTemplates(); }}
            />

            {/* Edit modal */}
            <MessageTemplateForm
                visible={!!editTemplate}
                template={editTemplate}
                onClose={() => setEditTemplate(null)}
                onSuccess={() => { setEditTemplate(null); fetchTemplates(); }}
            />

            {/* Preview modal */}
            <Modal
                title={previewTemplate ? `Preview: ${previewTemplate.key}` : ''}
                open={!!previewTemplate}
                onCancel={() => setPreviewTemplate(null)}
                footer={null}
                width={700}
            >
                {previewTemplate?.channel === 'email' ? (
                    <iframe
                        title="email-preview"
                        srcDoc={previewTemplate?.body || ''}
                        style={{ width: '100%', height: 500, border: '1px solid #eee', borderRadius: 6 }}
                    />
                ) : (
                    <p style={{ whiteSpace: 'pre-wrap', lineHeight: '22px' }}>
                        {previewTemplate?.body}
                    </p>
                )}
            </Modal>

            <Modal
                title="Confirm Deletion"
                open={!!deleteTemplate}
                onOk={handleDelete}
                onCancel={() => setDeleteTemplate(null)}
                okText="Yes, Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                confirmLoading={isDeleting}
            >
                <p>
                    Delete template <b>{deleteTemplate?.key}</b>? This cannot be undone.
                </p>
            </Modal>

            <Modal
                title={`Send ${sendTemplate?.channel === 'sms' ? 'SMS' : 'Email'} Template`}
                open={sendModal}
                onCancel={() => { setSendModal(false); setSendVariables({}); setActiveVariable(null); }}
                onOk={handleSendTemplate}
                confirmLoading={sendLoading}
                okText="Send"
                width={640}
            >
                <p>Send template <b>{sendTemplate?.key}</b> via {sendTemplate?.channel}.</p>

                <Form layout="vertical">
                    <Form.Item
                        label={sendTemplate?.channel === 'sms' ? 'Recipient phone number' : 'Recipient email address'}
                        validateStatus={sendError ? 'error' : ''}
                        help={sendError || ''}
                    >
                        <Input
                            value={sendRecipient}
                            onChange={(e) => { setSendRecipient(e.target.value); setSendError(''); }}
                            placeholder={sendTemplate?.channel === 'sms' ? '+1234567890' : 'user@example.com'}
                        />
                    </Form.Item>

                    <Form.Item label="Message preview">
                        {sendTemplate?.body ? (
                            sendTemplate.channel === 'email' ? (
                                <>
                                    <iframe
                                        key={JSON.stringify(sendVariables)}
                                        title="message-preview"
                                        srcDoc={resolveTemplateBody(sendTemplate.body, sendVariables)}
                                        style={{ width: '100%', height: 260, border: '1px solid #eee', borderRadius: 6 }}
                                    />
                                    {bodyVariables.length > 0 && (
                                        <div style={{ marginTop: 12 }}>
                                            {renderVariableChips(bodyVariables)}
                                            {renderVariableInputPanel()}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: 2,
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            padding: 12,
                                            border: '1px solid #eee',
                                            borderRadius: 6,
                                            background: '#fafafa',
                                            fontSize: 14,
                                        }}
                                    >
                                        {renderSmsWithVariables(
                                            sendTemplate.body,
                                            sendVariables,
                                            (key) => {
                                                setActiveVariable(key);
                                                setTimeout(() => inputRefs.current[key]?.focus(), 50);
                                            }
                                        )}
                                    </div>
                                    {bodyVariables.length > 0 && (
                                        <div style={{ marginTop: 12 }}>
                                            {renderVariableChips(bodyVariables)}
                                            {renderVariableInputPanel()}
                                        </div>
                                    )}
                                </>
                            )
                        ) : (
                            <span style={{ color: '#aaa' }}>No body available.</span>
                        )}
                    </Form.Item>
                </Form>
            </Modal>
        </Container>
    );
};

const mapStateToProps = (state) => ({
    type: state.auth && state.auth.user ? state.auth.user.type : null,
});

export default connect(mapStateToProps)(MessageTemplates);